import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import type { ReactNode } from "react";

import type { PartyShellPalette } from "../theme/theme";

export type BluffScoreRow = { name: string; pts: number };

/** Fun, translated phase line (emoji + copy). Fallback to phase string if key missing. */
export function bluffPhaseEmojiLabel(t: (key: string) => string, phase: string): string {
  const key = `party.playBluff.phase_${phase}`;
  const s = t(key);
  return s === key ? phase : s;
}

const rankMedal = (i: number) => (i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null);

function scoreRowSurface(partyPal: PartyShellPalette, rankIndex: number): SystemStyleObject<Theme> {
  if (rankIndex === 0) {
    return {
      border: `2px solid ${partyPal.gold}`,
      bgcolor: alpha(partyPal.gold, 0.12),
      boxShadow: `0 10px 28px -14px ${alpha(partyPal.gold, 0.55)}`,
    };
  }
  if (rankIndex === 1) {
    return {
      border: `1.5px solid ${alpha(partyPal.violet, 0.55)}`,
      bgcolor: alpha(partyPal.violet, 0.08),
      boxShadow: `0 8px 22px -14px ${alpha(partyPal.violet, 0.35)}`,
    };
  }
  if (rankIndex === 2) {
    return {
      border: `1.5px solid ${alpha("#c97b4a", 0.45)}`,
      bgcolor: alpha("#c97b4a", 0.08),
    };
  }
  return {
    border: `1px solid ${partyPal.inkFaint}`,
    bgcolor: alpha(partyPal.paperInk, 0.04),
  };
}

export function BluffScoresStrip({
  rows,
  title,
  partyPal,
}: {
  rows: BluffScoreRow[];
  title: string;
  partyPal: PartyShellPalette;
}) {
  if (rows.length === 0) return null;
  return (
    <Paper
      elevation={0}
      className="party-anim-fade-scale-in"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: "var(--r-card)",
        border: `1.5px solid ${alpha(partyPal.violet, 0.35)}`,
        background: `linear-gradient(165deg, ${alpha(partyPal.violet, 0.12)} 0%, rgba(255,255,255,0.06) 42%, rgba(0,0,0,0.18) 100%)`,
        boxShadow: "var(--shadow-card)",
      }}
    >
      <Typography
        className="party-mono"
        sx={{ fontSize: 10, letterSpacing: "0.2em", color: partyPal.gold, fontWeight: 800, textTransform: "uppercase", mb: 1.5 }}
      >
        ⚡ {title}
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.25}>
        {rows.map((r, i) => {
          const medal = rankMedal(i);
          return (
          <Box
            key={`${r.name}-${r.pts}-${i}`}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 2.5,
              minWidth: 100,
              flex: "1 1 92px",
              maxWidth: 160,
              ...scoreRowSurface(partyPal, i),
            }}
          >
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.35 }}>
              {medal ? (
                <Typography component="span" sx={{ fontSize: 15, lineHeight: 1 }}>
                  {medal}
                </Typography>
              ) : null}
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: partyPal.ink, lineHeight: 1.2 }} noWrap>
                {r.name}
              </Typography>
            </Stack>
            <Typography
              className="party-display"
              sx={{
                fontSize: i === 0 ? 26 : 22,
                fontWeight: 900,
                color: partyPal.gold,
                lineHeight: 1,
                letterSpacing: "-0.03em",
              }}
            >
              {r.pts}
            </Typography>
          </Box>
        );
        })}
      </Stack>
    </Paper>
  );
}

/** MUI `sx` fragment for vote option buttons (game board feel). */
export function bluffVoteOptionButtonSx(partyPal: PartyShellPalette): SystemStyleObject<Theme> {
  return {
    py: 2,
    px: 2,
    borderRadius: "var(--r-chip)",
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: alpha(partyPal.paperInk, 0.1),
    bgcolor: alpha(partyPal.paperInk, 0.03),
    fontWeight: 800,
    transition: "transform calc(0.12s * var(--motion)) ease, box-shadow 0.15s ease, border-color 0.15s ease",
    "&:hover": {
      borderColor: partyPal.violet,
      boxShadow: `0 0 0 3px ${alpha(partyPal.violet, 0.22)}, 0 12px 28px -16px ${alpha(partyPal.violet, 0.45)}`,
      transform: "translateY(-2px)",
    },
  };
}

export function BluffLeaderboardList({
  rows,
  partyPal,
  headline,
  winnerLine,
}: {
  rows: BluffScoreRow[];
  partyPal: PartyShellPalette;
  headline: string;
  winnerLine?: string | null;
}) {
  if (rows.length === 0) return null;
  return (
    <Stack spacing={1.25}>
      <Typography
        className="party-display"
        sx={{
          fontSize: "clamp(20px, 5vw, 26px)",
          fontWeight: 900,
          color: partyPal.paperInk,
          letterSpacing: "-0.02em",
          lineHeight: 1.2,
        }}
      >
        {headline}
      </Typography>
      <Stack spacing={1}>
        {rows.map((row, i) => {
          const medal = rankMedal(i);
          return (
          <Stack
            key={row.name + i}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 1.75,
              py: 1.35,
              borderRadius: 2.5,
              gap: 1.5,
              ...scoreRowSurface(partyPal, i),
            }}
          >
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                className="party-mono"
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: partyPal.paperInk,
                  opacity: 0.45,
                  width: 28,
                  flexShrink: 0,
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </Typography>
              {medal ? (
                <Typography component="span" sx={{ fontSize: 18, flexShrink: 0 }}>
                  {medal}
                </Typography>
              ) : null}
              <Typography sx={{ fontWeight: 800, color: partyPal.paperInk, fontSize: 16, lineHeight: 1.25 }} noWrap>
                {row.name}
              </Typography>
            </Stack>
            <Typography
              className="party-display"
              sx={{
                fontSize: i === 0 ? 28 : 24,
                fontWeight: 900,
                color: partyPal.hot,
                flexShrink: 0,
                letterSpacing: "-0.03em",
              }}
            >
              {row.pts}
            </Typography>
          </Stack>
        );
        })}
      </Stack>
      {winnerLine ? (
        <Box
          sx={{
            mt: 0.5,
            p: 2,
            borderRadius: 3,
            textAlign: "center",
            border: `2px solid ${partyPal.gold}`,
            background: `linear-gradient(135deg, ${alpha(partyPal.violet, 0.14)} 0%, ${alpha(partyPal.hot, 0.1)} 100%)`,
            boxShadow: `0 16px 40px -18px ${alpha(partyPal.gold, 0.45)}`,
          }}
        >
          <Typography className="party-display" sx={{ fontSize: "clamp(18px, 4.5vw, 24px)", fontWeight: 900, color: partyPal.paperInk, lineHeight: 1.35 }}>
            🏆 {winnerLine}
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );
}

/**
 * Primary play surface for Bluff — same language as Quick Questions / Dare / Category:
 * `party-kan-card`, cream `--paper`, `var(--paper-ink)` copy, flat header row + prompt block.
 */
export function BluffQuestionCard({
  phaseLabel,
  countdown,
  children,
  partyPal,
  questionHighlight,
  timerLive,
}: {
  phaseLabel: string;
  countdown?: string | null;
  children: ReactNode;
  partyPal: PartyShellPalette;
  questionHighlight?: string | null;
  /** When true and countdown shown, timer pill uses a soft pulse (see partyShell.css). */
  timerLive?: boolean;
}) {
  const hasBody = Boolean(children);
  const rule = `1px solid ${alpha(partyPal.paperInk, 0.1)}`;

  return (
    <Box
      className="party-kan-card party-bluff-card party-anim-fade-scale-in"
      sx={{
        "--card-bg": "var(--paper)",
        mb: 2,
        p: { xs: 2.25, sm: 2.75 },
        borderRadius: "var(--r-card)",
        color: "var(--paper-ink)",
        border: "1.5px solid color-mix(in oklab, var(--ink-faint) 55%, transparent)",
        boxShadow: "var(--shadow-card)",
        minHeight: hasBody || questionHighlight ? 160 : "auto",
      }}
    >
      <Stack spacing={0}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5} flexWrap="wrap" sx={{ pb: questionHighlight || hasBody ? 2 : 0 }}>
          <Typography
            className="party-display"
            sx={{
              fontSize: "clamp(15px, 3.9vw, 18px)",
              fontWeight: 900,
              lineHeight: 1.2,
              color: "var(--paper-ink)",
              flex: 1,
              minWidth: 0,
            }}
          >
            {phaseLabel}
          </Typography>
          {countdown != null && countdown !== "" ? (
            <Box
              className={timerLive ? "party-bluff-timer--live" : undefined}
              sx={{
                px: 1.75,
                py: 0.55,
                borderRadius: "var(--r-pill)",
                flexShrink: 0,
                bgcolor: "rgba(255,255,255,0.72)",
                border: "1.5px solid color-mix(in oklab, var(--hot) 42%, transparent)",
              }}
            >
              <Typography className="party-display" sx={{ fontSize: "clamp(19px, 4.5vw, 24px)", fontWeight: 900, color: "var(--hot)", lineHeight: 1 }}>
                {countdown}
              </Typography>
            </Box>
          ) : null}
        </Stack>

        {questionHighlight ? (
          <Box sx={{ pt: 2, borderTop: rule }}>
            <Typography
              component="div"
              className="party-display"
              sx={{
                textAlign: "center",
                fontSize: "clamp(22px, 6vw, 34px)",
                fontWeight: 900,
                lineHeight: 1.28,
                color: "var(--paper-ink)",
              }}
            >
              {questionHighlight}
            </Typography>
          </Box>
        ) : null}

        {hasBody ? (
          <Box sx={{ pt: 2, borderTop: questionHighlight ? rule : "none" }}>
            {children}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}
